import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { Home, ChevronRight } from 'lucide-react';
import { ALL_COURSE_CATEGORIES } from '@/config/courseNavigation';

// Utility function to get breadcrumb items from current path
const getBreadcrumbItems = (pathname: string, params: any) => {
  const segments = pathname.split('/').filter(Boolean);
  const items = [];

  // Always start with home
  items.push({
    label: 'Home',
    path: '/',
    icon: Home,
    isActive: false
  });

  // Handle dashboard
  if (pathname === '/dashboard') {
    items.push({
      label: 'Dashboard',
      path: '/dashboard',
      isActive: true
    });
    return items;
  }

  // Handle all courses page
  if (pathname === '/courses') {
    items.push({
      label: 'All Courses',
      path: '/courses',
      isActive: true
    });
    return items;
  }

  // Handle course category pages (e.g., /sql-courses, /python-courses)
  if (segments.length === 1 && segments[0].endsWith('-courses')) {
    const categoryId = segments[0].replace('-courses', '');
    const category = ALL_COURSE_CATEGORIES.find(cat => cat.id === categoryId);
    
    items.push({
      label: 'All Courses',
      path: '/courses',
      isActive: false
    });
    
    if (category) {
      items.push({
        label: category.title,
        path: pathname,
        isActive: true
      });
    }
    return items;
  }

  // Handle course detail pages (e.g., /sql-courses/basic-sql)
  if (segments.length === 2 && segments[0].endsWith('-courses')) {
    const categoryId = segments[0].replace('-courses', '');
    const category = ALL_COURSE_CATEGORIES.find(cat => cat.id === categoryId);
    const courseId = segments[1];
    
    items.push({
      label: 'All Courses',
      path: '/courses',
      isActive: false
    });
    
    if (category) {
      items.push({
        label: category.title,
        path: `/${segments[0]}`,
        isActive: false
      });
      
      const course = category.courses.find(c => c.id === courseId);
      items.push({
        label: course?.title || courseId.replace(/-/g, ' '),
        path: pathname,
        isActive: true
      });
    }
    return items;
  }

  // Handle module pages (e.g., /sql-courses/basic-sql/module-1)
  if (segments.length === 3 && segments[0].endsWith('-courses')) {
    const categoryId = segments[0].replace('-courses', '');
    const category = ALL_COURSE_CATEGORIES.find(cat => cat.id === categoryId);
    const courseId = segments[1];
    const moduleId = segments[2];
    
    items.push({
      label: 'All Courses',
      path: '/courses',
      isActive: false
    });
    
    if (category) {
      items.push({
        label: category.title,
        path: `/${segments[0]}`,
        isActive: false
      });
      
      const course = category.courses.find(c => c.id === courseId);
      items.push({
        label: course?.title || courseId.replace(/-/g, ' '),
        path: `/${segments[0]}/${courseId}`,
        isActive: false
      });
      
      items.push({
        label: moduleId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        path: pathname,
        isActive: true
      });
    }
    return items;
  }

  // Handle admin/staff pages
  if (segments[0] === 'admin') {
    items.push({
      label: 'Admin',
      path: '/admin',
      isActive: segments.length === 1
    });
    
    if (segments.length > 1) {
      items.push({
        label: segments[1].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        path: pathname,
        isActive: true
      });
    }
    return items;
  }

  if (segments[0] === 'staff') {
    items.push({
      label: 'Staff Portal',
      path: '/staff',
      isActive: segments.length === 1
    });
    
    if (segments.length > 1) {
      items.push({
        label: segments[1].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        path: pathname,
        isActive: true
      });
    }
    return items;
  }

  // Handle other single-level pages
  if (segments.length === 1) {
    const pageLabel = segments[0].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    items.push({
      label: pageLabel,
      path: pathname,
      isActive: true
    });
  }

  return items;
};

const BreadcrumbNavigation: React.FC = () => {
  const location = useLocation();
  const params = useParams();
  
  const breadcrumbItems = getBreadcrumbItems(location.pathname, params);

  // Don't show breadcrumbs on the home page
  if (location.pathname === '/') {
    return null;
  }

  return (
    <div className="px-6 py-3 border-b bg-background">
      <Breadcrumb>
        <BreadcrumbList>
          {breadcrumbItems.map((item, index) => (
            <React.Fragment key={item.path}>
              <BreadcrumbItem>
                {item.isActive ? (
                  <BreadcrumbPage className="flex items-center gap-1">
                    {item.icon && <item.icon className="h-3.5 w-3.5" />}
                    {item.label}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={item.path} className="flex items-center gap-1 hover:text-foreground">
                      {item.icon && <item.icon className="h-3.5 w-3.5" />}
                      {item.label}
                    </Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {index < breadcrumbItems.length - 1 && (
                <BreadcrumbSeparator>
                  <ChevronRight className="h-3.5 w-3.5" />
                </BreadcrumbSeparator>
              )}
            </React.Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
};

export default BreadcrumbNavigation;